/**
 * File: useAuthProviderController.ts
 * Path: apps/admin/src/modules/auth/hooks/useAuthProviderController.ts
 * Description: Firebase Authentication state for AuthProvider. Uses onIdTokenChanged so custom claim updates sync
 * automatically; returns the value the auth context holds.
 */

import { useEffect, useState } from "react";
import { User, onIdTokenChanged } from "firebase/auth";
import { auth } from "@idk/config";
import { roleFromClaims, type UserRole, type RoleClaims } from "@idk/types";

export interface AuthContextValue {
  user: User | null;
  role: UserRole | null;
  claims: RoleClaims | null;
  isLoading: boolean;
  refreshToken: () => Promise<void>;
}

export const useAuthProviderController = (): AuthContextValue => {
  const [user, setUser] = useState<User | null>(null);
  const [claims, setClaims] = useState<RoleClaims | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refreshToken = async () => {
    if (auth.currentUser) {
      try {
        // This force-refresh triggers onIdTokenChanged automatically,
        // which handles the state update cleanly in one centralized place.
        await auth.currentUser.getIdToken(true);
      } catch (error) {
        console.error("Failed to refresh token:", error);
      }
    }
  };

  useEffect(() => {
    // onIdTokenChanged fires on sign-in, sign-out, AND token refresh
    const unsubscribe = onIdTokenChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);

        try {
          // No force refresh needed here; onIdTokenChanged guarantees a fresh token
          const tokenResult = await currentUser.getIdTokenResult();
          const customClaims = tokenResult.claims;

          setClaims({
            admin: customClaims.admin === true,
            editor: customClaims.editor === true,
            role: roleFromClaims(customClaims),
          });
        } catch (error) {
          console.error("Failed to fetch custom claims:", error);
          setClaims(null);
        }
      } else {
        setUser(null);
        setClaims(null);
      }

      setIsLoading(false);
    });

    return () => unsubscribe();
  }, []);

  return {
    user,
    role: claims?.role || null,
    claims,
    isLoading,
    refreshToken,
  };
};
